import { useCallback, useState } from "react";
import { DataTable, type DataTableColumn } from "@shop-ledger/ui-components";
import { usePlatformClient } from "../../api/ClientContext";
import { MockBadge } from "../../components/MockBadge";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { PLATFORM_API_MODE } from "../../env";
import type { LogEntry, LogLevel } from "../../api/types";
import { LOG_LEVELS } from "../../api/types";

const LEVEL_BADGE_CLASS: Record<LogLevel, string> = {
  debug: "badge--fresh",
  info: "badge--active",
  warn: "badge--aged",
  error: "badge--suspended",
};

/**
 * §9.4's searchable/filterable centralized log view. Every entry carries the exact
 * correlation-key set §9.4 names: `event_id`, `tenant_id`, `device_id`, `request_id` —
 * columns below surface all four so an operator can trace one failed sync event or
 * payment end-to-end, per the spec's stated purpose.
 *
 * Permanently mock-badged regardless of which `PlatformApiClient` is active: no structured
 * log pipeline (correlation-id middleware, Loki/ELK-class aggregation) exists anywhere in
 * the real backend (`backend/BACKEND_ARCHITECTURE.md`'s "What's deferred"). In mock mode,
 * `mockPlatformApiClient.searchLogs()` returns a fixture array; `realPlatformApiClient`
 * always returns `[]` — this page shows an explicit "not available" note in that case
 * rather than letting an empty table read as "no matching log lines."
 */
export function LogsPage() {
  const client = usePlatformClient();
  const isRealMode = PLATFORM_API_MODE !== "mock";
  const [query, setQuery] = useState("");
  const [level, setLevel] = useState<LogLevel | "">("");
  const [tenantId, setTenantId] = useState("");

  const fetchLogs = useCallback(
    () =>
      client.searchLogs({
        query: query || undefined,
        level: level || undefined,
        tenantId: tenantId || undefined,
      }),
    [client, query, level, tenantId],
  );

  const { data, loading, error } = useAsyncData(fetchLogs, [fetchLogs]);

  const columns: DataTableColumn<LogEntry, keyof LogEntry>[] = [
    {
      key: "timestamp",
      header: "Time",
      render: (row) => new Date(row.timestamp).toLocaleString(),
    },
    {
      key: "level",
      header: "Level",
      render: (row) => <span className={`badge ${LEVEL_BADGE_CLASS[row.level]}`}>{row.level}</span>,
    },
    { key: "message", header: "Message", render: (row) => row.message },
    { key: "tenant_id", header: "Tenant ID", render: (row) => row.tenant_id ?? <span className="muted">—</span> },
    { key: "device_id", header: "Device ID", render: (row) => row.device_id ?? <span className="muted">—</span> },
    {
      key: "request_id",
      header: "Request ID",
      render: (row) => <span className="muted">{row.request_id.slice(0, 8)}…</span>,
    },
    { key: "event_id", header: "Event ID", render: (row) => <span className="muted">{row.event_id.slice(0, 8)}…</span> },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">
            Centralized Logs <MockBadge live={false} label={isRealMode ? "not available" : "mock"} title="Log search isn't available yet for this deployment." />
          </h1>
          <p className="page__subtitle">Searchable by business, level, and free text.</p>
        </div>
      </div>

      {isRealMode ? (
        <StatusBanner kind="warning">
          No log-aggregation pipeline exists in the real backend yet (no correlation-id middleware, no Loki/ELK-class
          stack — see ARCHITECTURE.md). This view will always be empty in real mode; switch to{" "}
          <code>VITE_PLATFORM_API_MODE=mock</code> to see fixture log data.
        </StatusBanner>
      ) : null}

      <div className="filter-bar panel">
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="log-query">
            Search message
          </label>
          <input
            id="log-query"
            className="sl-input"
            placeholder="e.g. NEGATIVE_STOCK"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="log-level">
            Level
          </label>
          <select
            id="log-level"
            className="sl-input"
            value={level}
            onChange={(e) => setLevel(e.target.value as LogLevel | "")}
          >
            <option value="">All levels</option>
            {LOG_LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="log-tenant">
            Tenant ID
          </label>
          <input
            id="log-tenant"
            className="sl-input"
            placeholder="filter by tenant id"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
          />
        </div>
      </div>

      {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}

      <div className="panel">
        <DataTable
          columns={columns}
          rows={data ?? []}
          getRowId={(row) => row.event_id}
          emptyMessage={loading ? "Loading…" : "No log entries match this filter."}
        />
      </div>
    </div>
  );
}
